import * as THREE from "three";

/**
 * Calculates the Moon's sub-lunar geographic point (the point on Earth
 * directly below the Moon) for a given Date, using Jean Meeus's simplified
 * low-precision algorithm ("Astronomical Algorithms", ch. 47).
 *
 * Returns { lat, lng } in degrees, suitable for globe.getCoords(lat, lng, alt).
 */
export function getMoonSubPoint(date = new Date()) {
  const toRad = (x) => (x * Math.PI) / 180;
  const toDeg = (x) => (x * 180) / Math.PI;

  // Days since J2000.0
  const d = date.getTime() / 86_400_000 - 10_957.5;

  // Fundamental arguments (degrees)
  const L = ((218.316 + 13.176_396 * d) % 360 + 360) % 360; // Mean longitude
  const M = ((134.963 + 13.064_993 * d) % 360 + 360) % 360; // Mean anomaly
  const F = (( 93.272 + 13.229_350 * d) % 360 + 360) % 360; // Arg of latitude

  // Low-precision ecliptic coordinates (degrees)
  const eclLon = L + 6.289 * Math.sin(toRad(M));
  const eclLat =     5.128 * Math.sin(toRad(F));

  // Obliquity of the ecliptic (degrees)
  const eps = 23.439 - 0.000_000_4 * d;

  // Ecliptic → equatorial
  const sinLon = Math.sin(toRad(eclLon));
  const cosLon = Math.cos(toRad(eclLon));
  const sinLat = Math.sin(toRad(eclLat));
  const cosLat = Math.cos(toRad(eclLat));
  const sinEps = Math.sin(toRad(eps));
  const cosEps = Math.cos(toRad(eps));

  const ra  = toDeg(Math.atan2(sinLon * cosEps - (sinLat / cosLat) * sinEps, cosLon));
  const dec = toDeg(Math.asin(sinLat * cosEps + cosLat * sinEps * sinLon));

  // Greenwich Mean Sidereal Time (degrees) — same formula as getGmstDegrees in sun.js
  let gmst = (280.460_618_37 + 360.985_647_366_29 * d) % 360;
  if (gmst < 0) gmst += 360;

  // Sub-lunar longitude = RA − GMST, normalised to [−180, 180]
  const lng = ((ra - gmst + 540) % 360) - 180;
  const lat = dec; // Sub-lunar latitude == Moon's declination

  return { lat, lng };
}

// Earthshine at new moon, as a fraction of the sunlit side's brightness.
const EARTHSHINE = "0.003";
// Lit fraction of Earth as seen from the Moon: 1 at new moon (Moon towards
// the Sun), 0 at full moon.
export const earthPhase = (moonDir, sunDir) => (1 + moonDir.dot(sunDir)) / 2;

// ---------------------------------------------------------------------------
// Custom phase shader
// Computes Lambert diffuse from the real sun direction, producing crescent /
// quarter / gibbous / full phases automatically. Earthshine keeps the
// shadowed side faintly visible near new moon (when it faces a fully lit
// Earth) and fades out towards full moon.
// ---------------------------------------------------------------------------
const MOON_VERT = /* glsl */`
  varying vec2 vUv;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPos;

  void main() {
    vUv = uv;
    vWorldNormal = normalize(mat3(modelMatrix) * normal);
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const MOON_FRAG = /* glsl */`
  uniform sampler2D uMap;
  uniform sampler2D uBumpMap;
  uniform vec3      uSunDir;     // unit vector pointing TOWARD the sun
  uniform float     uHasMap;     // 1.0 once texture is loaded
  uniform float     uHasBump;    // 1.0 once bump map is loaded
  uniform float     uBumpScale;  // height of bump perturbation
  uniform float     uBrightness; // multiplier for diffuse light
  uniform float     uEarthPhase; // lit fraction of Earth seen from the Moon

  varying vec2 vUv;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPos;

  // Perturb surface normal from a grayscale heightmap using screen-space
  // derivatives — no tangent attributes needed (same technique as THREE.js).
  vec3 perturbNormal(vec3 pos, vec3 N, float scale) {
    float h   = texture2D(uBumpMap, vUv).r;
    float dBx = dFdx(h);
    float dBy = dFdy(h);

    vec3 sigmaX = dFdx(pos);
    vec3 sigmaY = dFdy(pos);
    vec3 R1 = cross(sigmaY, N);
    vec3 R2 = cross(N, sigmaX);
    float det = dot(sigmaX, R1);

    vec3 grad = sign(det) * (dBx * R1 + dBy * R2);
    return normalize(abs(det) * N - scale * grad);
  }

  void main() {
    vec3 raw = uHasMap > 0.5
      ? texture2D(uMap, vUv).rgb
      : vec3(0.72, 0.72, 0.72); // grey fallback

    // Desaturate almost fully — the eye sees the moon as near-monochrome
    float luma = dot(raw, vec3(0.299, 0.587, 0.114));
    vec3 desaturated = mix(vec3(luma), raw, 0.15);

    // Compress contrast toward a mid-grey (naked-eye perception)
    vec3 surface = mix(vec3(0.72), desaturated, 0.55);

    // Perturb normal with bump map once loaded
    vec3 N = uHasBump > 0.5
      ? perturbNormal(vWorldPos, vWorldNormal, uBumpScale)
      : vWorldNormal;

    // Lambert diffuse — produces the phase terminator
    float NdotL  = max(0.0, dot(N, uSunDir));

    // Earthshine: a small fraction of the sunlit side, by Earth's phase.
    // Brighter than the real ~1e-4 so a crescent's dark side still shows.
    float ambient = uBrightness * ${EARTHSHINE} * uEarthPhase;
    float light   = ambient + NdotL * uBrightness;

    gl_FragColor = vec4(surface * light, 1.0);
  }
`;

// ---------------------------------------------------------------------------

/**
 * Builds the Moon mesh, attaches it to the scene, and adds a dim directional
 * light that shines from the Moon toward Earth — simulating moonlight.
 *
 * Returns:
 *   update(moonDir)       — reposition mesh + moonlight when date changes
 *   setSunDirection(dir)  — update the phase shader when the sun moves
 *   setBrightness(val)    — change moon intensity
 *   upgradeTextures()     — swap in the 4k maps (larger screens), before a close-up
 */
export const MOON_SCENE_DIST = 1_200; // units from Earth centre (~12 globe radii)
export const MOON_RADIUS     = 5.4;   // preserves real angular size at MOON_SCENE_DIST

export function createMoonMesh(scene, loader, initialMoonDir, initialSunDir, smallScreen = false) {
  // Phase shader material — works without a texture until it loads
  const uniforms = {
    uMap:        { value: null },
    uBumpMap:    { value: null },
    uSunDir:     { value: (initialSunDir ?? new THREE.Vector3(1, 0, 0)).clone().normalize() },
    uHasMap:     { value: 0.0 },
    uHasBump:    { value: 0.0 },
    uBumpScale:  { value: 0.03 },
    uBrightness: { value: 4.0 },
    uEarthPhase: { value: 0.0 },
  };
  const moonDir = initialMoonDir.clone().normalize();
  const updatePhase = () => { uniforms.uEarthPhase.value = earthPhase(moonDir, uniforms.uSunDir.value); };
  updatePhase();

  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader:   MOON_VERT,
    fragmentShader: MOON_FRAG,
  });

  // 2k maps up front: from Earth orbit the Moon is a few dozen pixels across.
  // The 4k ones (~5 MB) wait for upgradeTextures(), called on the way there.
  let generation = 0;
  const applied = { uMap: 0, uBumpMap: 0 };
  const loadMaps = (colourPath, bumpPath) => {
    const current = ++generation;
    // A late 2k map mustn't replace a 4k one that beat it.
    const stale = (tex, slot) => {
      if (current > applied[slot]) {
        applied[slot] = current;
        return false;
      }
      tex.dispose();
      return true;
    };
    loader.load(
      colourPath,
      (tex) => {
        if (stale(tex, "uMap")) return;
        tex.colorSpace = THREE.SRGBColorSpace;
        uniforms.uMap.value?.dispose();
        uniforms.uMap.value    = tex;
        uniforms.uHasMap.value = 1.0;
      },
      undefined,
      () => { /* silently keep the grey fallback */ }
    );
    loader.load(
      bumpPath,
      (tex) => {
        if (stale(tex, "uBumpMap")) return;
        tex.colorSpace = THREE.NoColorSpace;
        uniforms.uBumpMap.value?.dispose();
        uniforms.uBumpMap.value  = tex;
        uniforms.uHasBump.value  = 1.0;
      },
      undefined,
      () => { /* silently skip bump if unavailable */ }
    );
  };
  loadMaps("/moon2k.jpg", "/moon_bump2k.jpg");

  let upgraded = smallScreen;
  const upgradeTextures = () => {
    if (upgraded) return;
    upgraded = true;
    loadMaps("/moon4k.jpg", "/moon_bump.jpg");
  };

  const geo  = new THREE.SphereGeometry(MOON_RADIUS, 64, 64);
  const mesh = new THREE.Mesh(geo, material);
  mesh.renderOrder = 0;

  // Moonlight — very dim, cool blue-white directional light aimed at Earth.
  // Intensity 0.15 gives a subtle fill on the night side.
  const moonLight = new THREE.DirectionalLight(0xc8d8ff, 0.075);
  moonLight.target.position.set(0, 0, 0);
  scene.add(moonLight);
  scene.add(moonLight.target);

  // Align the mesh so the near side (u=0.5 = local +X) always faces Earth.
  // lookAt(origin) makes local +Z face Earth; rotateY(-π/2) brings +X there.
  const tideLock = (dir) => {
    mesh.lookAt(new THREE.Vector3(0, 0, 0));
    mesh.rotateY(-Math.PI / 2);
  };

  // Initial placement
  const initialPos = initialMoonDir.clone().multiplyScalar(MOON_SCENE_DIST);
  mesh.position.copy(initialPos);
  moonLight.position.copy(initialPos);
  tideLock();
  scene.add(mesh);

  // Reposition mesh + moonlight when the moon's direction changes
  const update = (dir) => {
    moonDir.copy(dir).normalize();
    const pos = moonDir.clone().multiplyScalar(MOON_SCENE_DIST);
    mesh.position.copy(pos);
    moonLight.position.copy(pos);
    tideLock();
    updatePhase();
  };

  // Update the phase shader's sun direction (call whenever sun or date changes)
  const setSunDirection = (sunDir) => {
    uniforms.uSunDir.value.copy(sunDir).normalize();
    updatePhase();
  };

  const setBrightness = (val) => {
    uniforms.uBrightness.value = val;
  };

  return { mesh, light: moonLight, update, setSunDirection, setBrightness, upgradeTextures };
}
